<template>
  <div
    class="accounts-tab"
    :class="{ 'accounts-tab--assignment': isAssignmentVariant }"
    data-testid="setup-tasklist__accounts-tab"
  >
    <div class="accounts-tab__selected" data-testid="setup-tasklist__accounts-selected">
      <h5 class="accounts-tab__title ma-0" data-testid="setup-tasklist__accounts-title">
        Assign Accounts
      </h5>

      <p
        v-if="isAssignmentVariant"
        class="accounts-tab__hint ma-0"
        data-testid="setup-tasklist__accounts-hint"
      >
        {{ assignmentAccountsHint }}
      </p>

      <div
        v-if="!isAssignmentVariant && selectedAccounts.length"
        class="accounts-tab__chips-wrap"
        data-testid="setup-tasklist__accounts-chips"
      >
        <div class="accounts-tab__chips d-flex flex-wrap">
          <v-chip
            v-for="account in visibleSelectedAccounts"
            :key="account.selectionKey"
            close
            outlined
            class="accounts-tab__chip"
            data-testid="setup-tasklist__accounts-chip"
            @click:close="toggleAccount(account, false)"
          >
            <span class="accounts-tab__chip-text">{{ account.label }}</span>
          </v-chip>
        </div>
        <button
          v-if="hasMoreSelectedAccounts"
          type="button"
          class="accounts-tab__chips-toggle"
          data-testid="setup-tasklist__accounts-chips-toggle"
          @click="chipsExpanded = !chipsExpanded"
        >
          {{ chipsExpanded ? 'Show less' : 'Show more' }}
        </button>
      </div>
    </div>

    <div class="accounts-tab__picker" data-testid="setup-tasklist__accounts-picker">
      <p
        v-if="!isAssignmentVariant"
        class="accounts-tab__hint ma-0"
        data-testid="setup-tasklist__accounts-hint"
      >
      The available accounts below are automatically filtered based on the selected location.
      </p>

      <v-text-field
        v-model="search"
        dense
        outlined
        hide-details
        clearable
        prepend-inner-icon="mdi-magnify"
        :placeholder="
          isAssignmentVariant
            ? 'Search By Account Number Or Name'
            : 'Search by account number or name'
        "
        class="accounts-tab__search"
        data-testid="setup-tasklist__accounts-search"
      />

      <div
        v-if="isAssignmentVariant && selectedAccounts.length"
        class="accounts-tab__chips-wrap"
        data-testid="setup-tasklist__accounts-chips"
      >
        <div class="accounts-tab__chips d-flex flex-wrap">
          <v-chip
            v-for="account in visibleSelectedAccounts"
            :key="account.selectionKey"
            close
            outlined
            class="accounts-tab__chip"
            data-testid="setup-tasklist__accounts-chip"
            @click:close="toggleAccount(account, false)"
          >
            <span class="accounts-tab__chip-text">{{ account.label }}</span>
          </v-chip>
        </div>
        <button
          v-if="hasMoreSelectedAccounts"
          type="button"
          class="accounts-tab__chips-toggle"
          data-testid="setup-tasklist__accounts-chips-toggle"
          @click="chipsExpanded = !chipsExpanded"
        >
          {{ chipsExpanded ? 'Show less' : 'Show more' }}
        </button>
      </div>

      <div class="accounts-tab__table-wrap">
        <table class="accounts-tab__table" data-testid="setup-tasklist__accounts-table">
          <thead>
            <tr>
              <th class="accounts-tab__check-col">
                <v-checkbox
                  :input-value="allFilteredSelected"
                  :indeterminate="someFilteredSelected && !allFilteredSelected"
                  dense
                  hide-details
                  color="caccounting"
                  class="ma-0 pa-0"
                  data-testid="setup-tasklist__accounts-select-all"
                  @change="toggleSelectAll"
                />
              </th>
              <th>GL Code</th>
              <th>Account Name</th>
              <th>Type</th>
              <th>Category</th>
              <th>{{ locationHeader }}</th>
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="account in filteredAccounts"
              :key="account.selectionKey"
              :class="{ 'accounts-tab__row--selected': isSelected(account) }"
              data-testid="setup-tasklist__accounts-row"
              @click="toggleAccount(account, !isSelected(account))"
            >
              <td class="accounts-tab__check-col" @click.stop>
                <v-checkbox
                  :input-value="isSelected(account)"
                  dense
                  hide-details
                  color="caccounting"
                  class="ma-0 pa-0"
                  @change="toggleAccount(account, $event)"
                />
              </td>
              <td>{{ account.glcode || '—' }}</td>
              <td>{{ account.name || '—' }}</td>
              <td>{{ account.type || '—' }}</td>
              <td>{{ account.category || '—' }}</td>
              <td>
                <div
                  class="accounts-tab__location-cell"
                  data-testid="setup-tasklist__accounts-location-cell"
                >
                  <OverflowList
                    :items="account.locationNames"
                    data-testid="setup-tasklist__accounts-location-overflow"
                  />
                </div>
              </td>
            </tr>
            <tr v-if="!filteredAccounts.length">
              <td colspan="6" class="accounts-tab__empty">
                {{ emptyAccountsMessage }}
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>

    <p v-if="errors.glcode" class="error--text text-caption mt-2">{{ errors.glcode }}</p>
  </div>
</template>

<script>
import './AccountsTab.scss';
import { OverflowList } from '@/modules/financialclose/components';
import { ASSIGN_ACCOUNTS_HINT } from '@/modules/financialclose/utils/constants';
import {
  chartOfAccountLocationEntries,
  locationEntriesToNames,
} from '../../../ChartOfAccount/ChartOfAccountUtils';

/** Default visible chips (~4 rows / ~15 badges) before Show more. */
const VISIBLE_CHIPS_LIMIT = 15;

/**
 * API expects account number strings (`coa.glcode`), not COA Mongo `_id`.
 */
function toGlcodeValue(account) {
  if (!account) return '';
  const value = account.glcode != null && String(account.glcode).trim()
    ? String(account.glcode).trim()
    : '';
  return value;
}

function accountMatchesSelection(account, selectedSet) {
  if (!account || !selectedSet?.size) return false;
  const candidates = [account.glcode, account._id, account.id]
    .filter((v) => v != null && String(v).trim())
    .map((v) => String(v));
  return candidates.some((key) => selectedSet.has(key));
}

function resolveLocationNames(account = {}, locations = []) {
  const entries = chartOfAccountLocationEntries(account);
  if (entries.length && (locations || []).length) {
    const named = locationEntriesToNames(entries, locations);
    if (named.length) return named;
  }

  const raw = account.location;
  if (!raw) return [];
  if (typeof raw === 'string') {
    return raw
      .split(',')
      .map((s) => s.trim())
      .filter(Boolean);
  }
  if (!Array.isArray(raw)) return [];
  return raw
    .map((item) => {
      if (typeof item === 'string') return item.trim();
      return (item?.name || item?.location || item?.description || '').trim();
    })
    .filter(Boolean);
}

function buildLocationDisplay(names = []) {
  const list = (names || []).filter(Boolean);
  if (!list.length) {
    return {
      locationPrimary: '',
      locationOverflowCount: 0,
      locationOverflowItems: [],
    };
  }
  return {
    locationPrimary: list[0],
    locationOverflowCount: Math.max(0, list.length - 1),
    locationOverflowItems: list.slice(1),
  };
}

export default {
  name: 'AccountsTab',

  components: {
    OverflowList,
  },

  props: {
    glCodes: {
      type: Array,
      default: () => [],
    },
    chartOfAccounts: {
      type: Array,
      default: () => [],
    },
    errors: {
      type: Object,
      default: () => ({}),
    },
    /** `assignment` = Figma Assignment-block layout (hint → search → chips → table). */
    variant: {
      type: String,
      default: 'default',
      validator: (v) => ['default', 'assignment'].includes(v),
    },
    /**
     * Assignment variant only: parent tells us whether locations are selected so we
     * can differentiate "no locations picked yet" vs. "locations picked but no matches".
     */
    hasSelectedLocations: {
      type: Boolean,
      default: false,
    },
    /**
     * Assignment variant: a selected location has master type Consolidated,
     * so the picker lists the Accounts-step pool from any entity.
     */
    consolidatedLocationSelected: {
      type: Boolean,
      default: false,
    },
    locations: {
      type: Array,
      default: () => [],
    },
  },

  data() {
    return {
      localGlCodes: [...(this.glCodes || [])].map((v) => String(v)),
      search: '',
      chipsExpanded: false,
    };
  },

  computed: {
    isAssignmentVariant() {
      return this.variant === 'assignment';
    },
    assignmentAccountsHint() {
      return this.consolidatedLocationSelected
        ? ASSIGN_ACCOUNTS_HINT.CONSOLIDATED
        : ASSIGN_ACCOUNTS_HINT.FILTERED_BY_LOCATIONS;
    },
    locationHeader() {
      return this.$store.state.LocationHeader || 'Location';
    },
    coaOptions() {
      return (this.chartOfAccounts || [])
        .map((c) => {
          const glcode = toGlcodeValue(c);
          if (!glcode) return null;
          const locationNames = resolveLocationNames(c, this.locations);
          const locationDisplay = buildLocationDisplay(locationNames);
          return {
            ...c,
            glcode,
            type: c.type || c.account_type || '',
            category: c.category || '',
            selectionKey: glcode,
            label: [glcode, c.name].filter(Boolean).join(' - '),
            locationNames,
            ...locationDisplay,
          };
        })
        .filter(Boolean);
    },
    selectedSet() {
      return new Set((this.localGlCodes || []).map(String));
    },
    selectedAccounts() {
      return this.coaOptions.filter((a) => accountMatchesSelection(a, this.selectedSet));
    },
    visibleSelectedAccounts() {
      if (this.chipsExpanded) return this.selectedAccounts;
      return this.selectedAccounts.slice(0, VISIBLE_CHIPS_LIMIT);
    },
    hasMoreSelectedAccounts() {
      return this.selectedAccounts.length > VISIBLE_CHIPS_LIMIT;
    },
    filteredAccounts() {
      const q = (this.search || '').trim().toLowerCase();
      if (!q) return this.coaOptions;
      return this.coaOptions.filter((a) => {
        const hay = [
          a.glcode,
          a.name,
          a.category,
          a.type,
          a.label,
          ...(a.locationNames || []),
        ]
          .filter(Boolean)
          .join(' ')
          .toLowerCase();
        return hay.includes(q);
      });
    },
    allFilteredSelected() {
      const rows = this.filteredAccounts;
      if (!rows.length) return false;
      return rows.every((a) => this.isSelected(a));
    },
    someFilteredSelected() {
      return this.filteredAccounts.some((a) => this.isSelected(a));
    },
    emptyAccountsMessage() {
      if (!this.isAssignmentVariant) return 'No accounts found';
      if (this.consolidatedLocationSelected) {
        if (!(this.chartOfAccounts || []).length) {
          if (this.hasSelectedLocations) {
            return 'No accounts from the Accounts step are available';
          }
          return `Select ${this.locationHeader} to see accounts from the Accounts step`;
        }
        return 'No accounts found';
      }
      if (!(this.chartOfAccounts || []).length) {
        if (this.hasSelectedLocations) {
          return `No accounts from the Accounts step are mapped to the selected ${this.locationHeader}`;
        }
        return `Select ${this.locationHeader} to see accounts mapped to them from the Accounts step`;
      }
      return `No accounts found for the selected ${this.locationHeader}`;
    },
  },

  watch: {
    glCodes(val) {
      this.localGlCodes = [...(val || [])].map((v) => String(v));
    },
    selectedAccounts(accounts) {
      if ((accounts || []).length <= VISIBLE_CHIPS_LIMIT) {
        this.chipsExpanded = false;
      }
    },
  },

  beforeDestroy() {
    this.search = '';
    this.chipsExpanded = false;
    this.localGlCodes = [];
  },

  methods: {
    isSelected(account) {
      return accountMatchesSelection(account, this.selectedSet);
    },

    emitUpdate() {
      this.$emit('update:glCodes', [...this.localGlCodes]);
    },

    toggleAccount(accountOrId, checked) {
      const account =
        typeof accountOrId === 'object'
          ? accountOrId
          : this.coaOptions.find(
              (a) =>
                String(a.glcode) === String(accountOrId) ||
                String(a._id) === String(accountOrId)
            );

      const glcode = toGlcodeValue(account) || String(accountOrId || '');
      if (!glcode) return;

      const next = new Set(this.localGlCodes.map(String));
      // Drop legacy _id selections for this account when toggling
      if (account) {
        [account._id, account.id].forEach((id) => {
          if (id != null) next.delete(String(id));
        });
      }
      if (checked) next.add(glcode);
      else next.delete(glcode);

      this.localGlCodes = Array.from(next);
      this.emitUpdate();
    },

    toggleSelectAll(checked) {
      const next = new Set(this.localGlCodes.map(String));
      this.filteredAccounts.forEach((a) => {
        [a._id, a.id].forEach((id) => {
          if (id != null) next.delete(String(id));
        });
        if (checked) next.add(String(a.glcode));
        else next.delete(String(a.glcode));
      });
      this.localGlCodes = Array.from(next);
      this.emitUpdate();
    },
  },
};
</script>
